/**
 * DXFExporter - Export cut patterns as DXF (AutoCAD R12)
 *
 * Hand-rolled minimal R12 writer: each part ring becomes a closed
 * POLYLINE entity on a layer named after the part. R12 is the most
 * widely accepted flavour for laser/CNC toolchains. Units: millimetres.
 * Y is flipped (DXF Y-up vs sketch Y-down) so parts read correctly.
 *
 * Replaces ToolPathWriter/DXFWriter.java.
 */

import { packParts, packPartsPaged } from './ShapePacker.js';

/** One DXF group code/value pair per line */
function pair(code, value) {
    return `${code}\n${value}\n`;
}

/**
 * A closed POLYLINE entity from a ring
 */
function polylineEntity(ring, dx, dy, heightMM, layer) {
    let out = pair(0, 'POLYLINE') + pair(8, layer) + pair(66, 1) + pair(70, 1);
    for (const p of ring) {
        out += pair(0, 'VERTEX') + pair(8, layer);
        out += pair(10, (p.x + dx).toFixed(3));
        out += pair(20, (heightMM - (p.y + dy)).toFixed(3)); // flip Y
        out += pair(30, '0.0');
    }
    out += pair(0, 'SEQEND');
    return out;
}

/**
 * Build a complete DXF document from fabrication parts.
 *
 * Without a `page` option the whole layout goes onto one sheet growing
 * downward (packParts). With `page: n` (0-based) the parts are paginated
 * with ShapePacker.packPartsPaged ({sheetWidth, sheetHeight, ...}) and
 * only that page's parts are written; the requested page index is
 * clamped to the available range and echoed back as `page`.
 *
 * @param {Array<{name, polygons}>} parts - From SVGExporter.collectParts
 * @param {Object} [packOpts] - ShapePacker options ({sheetWidth, sheetHeight,
 *   gap, margin}) plus the optional `page` index
 * @returns {{dxf: string, widthMM: number, heightMM: number, partCount: number,
 *   pageCount: number, page: number}}
 */
export function buildDXF(parts, packOpts = {}) {
    const { page = null, ...opts } = packOpts;

    let placed, widthMM, heightMM;
    let pageCount = 1;
    let pageIndex = 0;

    if (page !== null && page !== undefined) {
        const paged = packPartsPaged(parts, opts);
        pageCount = paged.pageCount;
        pageIndex = Math.max(0, Math.min(page, pageCount - 1));
        ({ placed, widthMM, heightMM } = paged.pages[pageIndex] ||
            { placed: [], widthMM: 0, heightMM: 0 });
    } else {
        ({ placed, widthMM, heightMM } = packParts(parts, opts));
    }

    let entities = '';
    for (const { part, dx, dy } of placed) {
        for (const polygon of part.polygons) {
            entities += polylineEntity(polygon.outer, dx, dy, heightMM, part.name);
            for (const hole of polygon.holes || []) {
                entities += polylineEntity(hole, dx, dy, heightMM, part.name);
            }
        }
    }

    const dxf =
        pair(999, 'SketchChair.js cut pattern - millimetres') +
        pair(0, 'SECTION') + pair(2, 'HEADER') +
        pair(9, '$ACADVER') + pair(1, 'AC1009') +
        pair(9, '$INSUNITS') + pair(70, 4) + // 4 = millimetres
        pair(0, 'ENDSEC') +
        pair(0, 'SECTION') + pair(2, 'ENTITIES') +
        entities +
        pair(0, 'ENDSEC') +
        pair(0, 'EOF');

    return { dxf, widthMM, heightMM, partCount: placed.length, pageCount, page: pageIndex };
}
